import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check, Zap, ShieldCheck, Crown } from "lucide-react";
import { motion } from "framer-motion";
import PayPalCheckout from "../components/PayPalCheckout";
import PayPalProvider from "../components/PayPalProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { sentinel: ShieldCheck, guardian: Zap, bespoke: Crown };

export default function Retainer() {
  const [pkgs, setPkgs] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => { axios.get(`${API}/retainer-packages`).then((r) => setPkgs(r.data.packages || [])); }, []);

  async function subscribe(pkgId) {
    if (!email || !name) { toast.error("Provide name & email before subscribing."); return; }
    setLoadingId(pkgId);
    try {
      const res = await axios.post(`${API}/checkout/session`, { package_id: pkgId, origin_url: window.location.origin, customer_email: email, customer_name: name });
      if (res.data.url) window.location.href = res.data.url;
    } catch { toast.error("Checkout unavailable — please try again."); setLoadingId(null); }
  }

  return (
    <PayPalProvider mode="subscription">
    <div data-testid="retainer-page" style={{ background: "var(--ivory)" }}>
      <section className="pt-16 pb-10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--muted)" }}>Chapter Four · Recurring Partnership</div>
          <h1 className="font-display mt-4" style={{ fontSize: "clamp(3rem, 8vw, 7rem)", lineHeight: 0.9, letterSpacing: "-0.04em" }}>
            The <span className="font-display-italic" style={{ color: "var(--brass)" }}>retainer</span> plans.
          </h1>
          <p className="mt-8 font-body text-xl max-w-2xl" style={{ color: "var(--navy-soft)" }}>
            The stress-free path. Lock in a monthly cadence, receive photo debriefs after every visit, and stop thinking about exterior maintenance.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="thin-border p-6 md:p-8 mb-10 grid sm:grid-cols-2 gap-4" style={{ background: "var(--ivory-2)" }}>
            <input data-testid="retainer-name" className="field-boxed" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
            <input data-testid="retainer-email" className="field-boxed" placeholder="Business email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {pkgs.map((p, i) => {
              const Icon = ICONS[p.id] || ShieldCheck;
              const featured = p.id === "guardian";
              const bg = featured ? "var(--navy)" : "var(--ivory)";
              const fg = featured ? "var(--ivory)" : "var(--navy)";
              const muted = featured ? "rgba(248,247,244,0.6)" : "var(--muted)";
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: i * 0.08 }}
                  className="thin-border p-8 flex flex-col relative"
                  data-testid={`retainer-card-${p.id}`}
                  style={{ background: bg, color: fg, borderColor: featured ? "var(--navy)" : "var(--line)" }}
                >
                  {featured && (
                    <div className="absolute -top-3 left-6 pill" style={{ background: "var(--brass)", color: "var(--text)", borderColor: "var(--brass)" }}>Most chosen</div>
                  )}
                  <Icon size={28} style={{ color: featured ? "var(--brass-soft)" : "var(--brass)" }} />
                  <h3 className="font-display text-4xl mt-4" style={{ letterSpacing: "-0.03em" }}>{p.name}</h3>
                  <p className="text-sm mt-2 min-h-[3.5rem]" style={{ color: muted }}>{p.description}</p>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="font-display text-6xl" style={{ letterSpacing: "-0.03em" }}>${Math.round(p.amount)}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: muted }}>/ mo</span>
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.24em] mt-1" style={{ color: muted }}>USD · First month billed today</div>

                  <ul className="mt-6 space-y-3">
                    {(p.features || []).map((f, idx) => (
                      <li key={idx} className="flex items-start gap-3 font-body text-sm">
                        <Check size={14} className="mt-1 shrink-0" style={{ color: featured ? "var(--brass-soft)" : "var(--brass)" }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`subscribe-${p.id}`}
                    disabled={loadingId === p.id}
                    onClick={() => subscribe(p.id)}
                    className="mt-8 w-full justify-center"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "0.6rem",
                      background: featured ? "var(--brass)" : "var(--navy)",
                      color: featured ? "var(--navy)" : "var(--ivory)",
                      fontFamily: "'Space Mono', monospace",
                      fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.24em",
                      padding: "1rem 1.5rem", border: `1px solid ${featured ? "var(--brass)" : "var(--navy)"}`,
                      cursor: "pointer",
                    }}
                  >
                    {loadingId === p.id ? "Redirecting…" : "Subscribe"} <ArrowUpRight size={14} />
                  </button>

                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex-1" style={{ height: 1, background: featured ? "rgba(248,247,244,0.15)" : "var(--line)" }} />
                    <span className="font-mono text-[9px] uppercase tracking-[0.28em]" style={{ color: muted }}>or</span>
                    <div className="flex-1" style={{ height: 1, background: featured ? "rgba(248,247,244,0.15)" : "var(--line)" }} />
                  </div>

                  <div className="mt-3">
                    <PayPalCheckout
                      testId={`paypal-retainer-${p.id}`}
                      subscription={true}
                      orderPayload={{ package_id: p.id, customer_email: email, customer_name: name }}
                      validate={() => Boolean(name && email)}
                      validateMsg="Provide name & email before paying with PayPal."
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>

          <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--muted)" }}>
            Auto-renews · Cancel anytime · Payments secured by Stripe or PayPal · Confirmation within 24 hours
          </p>
        </div>
      </section>
    </div>
    </PayPalProvider>
  );
}
